import React, { useRef, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { useOrderOverlay } from "../../../hooks/useOrderOverlay";
import { useOrderDefaults } from "../../../hooks/useOrderDefaults";
import { useOrderStore } from "../../../store/orderStore.jsx";
import useQuantityModalStore from "../../../store/quantityModalStore.jsx";
import OrderInteractive from "./OrderInteractive";
import OCOConnectorLine from "./components/OCOConnectorLine";
import PositionLine from "./components/PositionLine";
import PositionLabel from "./components/PositionLabel";
import HiddenOrdersIndicator from "./components/HiddenOrdersIndicator";
import { RouterLoginModal } from "../../../components/Modals";
import { validateOrderReplacement } from "../../../utils/orderReplacementService";
import { commitPendingOTOCore } from "./commitPendingOTOCore";
import { usePositionData } from "../../../hooks/usePositionData";
import { validateOTOPrice } from "../../../utils/otoValidation";
import { getTickSize } from "../../../utils/priceUtils";
import {
  confirmNewOrder,
  confirmOrderModification,
  confirmOrderDeletion,
} from "../../../utils/confirmationUtils";

// InteractiveOrderIntegration - Integration wrapper that connects OrderInteractive with order store
// This component bridges the chart system with the order management system
const InteractiveOrderIntegration = ({
  symbol,
  enabled = true,
  onDragStateChange,
  currentPrice,
  ...otherProps
}) => {
  // Get order data and actions from the order store (by symbol)
  const {
    orders,
    createOrder,
    updateOrderPrice,
    updateOrderQuantity,
    submitOrder,
    removeOrder,
    createOTO,
    updateOTOPrice,
    removeOTO,
    removeAllOTOs,
  } = useOrderOverlay(symbol);

  // Get pending modifications for visual feedback
  const pendingModifications = useOrderStore(
    (state) => state.pendingModifications,
  );

  // Get position data for this symbol
  const position = usePositionData(symbol);

  // State to track if any order is being dragged (to hide OCO connectors during drag)
  const [isDragging, setIsDragging] = useState(false);

  // Ref to store the last known prices of orders to detect changes during drag operations
  // PoC #popout: SVG anchor to resolve the chart's current document at render
  const anchorNodeRef = useRef(null);
  const lastKnownPrices = useRef(new Map());
  // Ref to store the original relative distances between main orders and their OTO orders
  const otoRelativeDistances = useRef(new Map());
  // Ref to hold deferred OTO commits for open orders awaiting confirmation
  const deferredOTOCommits = useRef(new Map());

  // Commit pending OTO updates to store after drag ends
  const commitPendingOTOUpdates = (orderId) => {
    const pendingUpdates = otoRelativeDistances.current.get(
      `${orderId}_pending`,
    );
    if (pendingUpdates) {
      // Decisao+despacho extraidos para commitPendingOTOCore (teste unitario puro).
      // Descarta precos NaN/Infinity sem corromper o store; onSkip emite o aviso.
      commitPendingOTOCore({
        orderId,
        pendingUpdates,
        orders,
        validateOrderReplacement,
        updateOTOPrice,
        onSkip: (otoOrderId) =>
          console.warn(
            "⚠️ commitPendingOTOUpdates: Skipping NaN price for OTO:",
            otoOrderId,
          ),
      });

      // Delay deletion of _pending to allow React to propagate order store updates
      // This prevents visual flicker where _pending is gone but order.price hasn't updated yet
      setTimeout(() => {
        otoRelativeDistances.current.delete(`${orderId}_pending`);
      }, 50);
    }
  };

  // Get default order settings
  const { getAdjustedQuantity } = useOrderDefaults();

  // NOTE: Quantity edit modal state has been lifted to ChartWrapper to survive ChartCanvas remounts
  // The onQuantityModalOpen callback is used instead

  // Modal state for router login
  const [showLoginModal, setShowLoginModal] = React.useState(false);

  // Handle order creation (called when user clicks on chart)
  const handleCreateOrder = (orderSymbol, side, price, quantity) => {
    // Use adjusted quantity for the symbol
    const adjustedQuantity = getAdjustedQuantity(orderSymbol) || quantity;

    return createOrder(orderSymbol, side, price, adjustedQuantity);
  };

  // Handle order price updates (called during drag)
  const handleUpdateOrderPrice = async (orderId, newPrice) => {
    // Find the order to check for actual changes
    const order = orders.find((o) => o.id === orderId);
    if (!order) {
      console.warn("❌ Order not found for price update:", orderId);
      return;
    }

    // Use centralized validation to check if price actually changed
    const validation = validateOrderReplacement({
      dragStart: null, // We don't have drag coordinates in this context
      dragEnd: null,
      originalOrder: order,
      newValues: { price: newPrice },
      operationType: "main-order",
    });

    if (validation.hasChanges) {
      // For open orders (broker-bound), ask for confirmation before sending replaceOrder
      if (order.state === "open") {
        const confirmed = await confirmOrderModification(order, newPrice);
        if (!confirmed) {
          // Discard deferred OTO commits — they were never written to the store
          deferredOTOCommits.current.delete(orderId);
          return;
        }

        // Apply deferred OTO commits now that user confirmed
        const deferred = deferredOTOCommits.current.get(orderId);
        if (deferred) {
          deferred.forEach((pendingData, otoOrderId) => {
            const otoPrice =
              typeof pendingData === "object" ? pendingData.price : pendingData;
            const storedDeltaTicks =
              typeof pendingData === "object"
                ? pendingData.deltaTicks
                : undefined;
            updateOTOPrice(orderId, otoOrderId, otoPrice, {
              programmatic: true,
              skipRouterCall: true,
              deltaTicks: storedDeltaTicks,
            });
          });
          deferredOTOCommits.current.delete(orderId);
        }
      }

      console.log(
        "✅ InteractiveOrderIntegration.handleUpdateOrderPrice: Validation passed, calling updateOrderPrice:",
        {
          orderId,
          oldPrice: order.price,
          newPrice,
          orderState: order.state,
        },
      );

      // Store the price for future reference
      lastKnownPrices.current.set(orderId, newPrice);

      // Update the main order only - OTO orders are handled by handleDragStateChange in real-time
      return updateOrderPrice(orderId, newPrice, currentPrice);
    } else {
      console.warn(
        "⚠️ InteractiveOrderIntegration.handleUpdateOrderPrice: Validation failed - no changes detected:",
        {
          orderId,
          originalPrice: order.price,
          newPrice,
          validation,
        },
      );
      return Promise.resolve(); // Return resolved promise to maintain API consistency
    }
  };

  // Handle order submission (called on double-click or explicit submit)
  const handleSubmitOrder = async (orderId) => {
    const order = orders.find((o) => o.id === orderId);
    if (order) {
      const confirmed = await confirmNewOrder(order);
      if (!confirmed) return;
    }

    const result = await submitOrder(orderId, () => {
      setShowLoginModal(true);
    });
    return result;
  };

  // Handle order deletion (called when trash button is clicked)
  const handleDeleteOrder = async (orderId) => {
    const order = orders.find((o) => o.id === orderId);

    // Only confirm for broker-bound orders (open/pending), not local drafts
    if (order && (order.state === "open" || order.state === "pending")) {
      const confirmed = await confirmOrderDeletion(order);
      if (!confirmed) return;
    }

    removeOrder(orderId);
  };

  // Handle order quantity updates
  const handleUpdateOrderQuantity = (orderId, newQuantity) => {
    updateOrderQuantity(orderId, newQuantity);
  };

  // Get the Zustand store action for opening the quantity modal
  const openQuantityModal = useQuantityModalStore((state) => state.openModal);

  // Handle quantity edit - use Zustand store (global state survives all remounts)
  const handleQuantityEdit = (order) => {
    // Use the global Zustand store - survives any component remounts
    openQuantityModal(order);
  };

  // NOTE: handleQuantityModalConfirm and handleQuantityModalHide have been moved to ChartWrapper
  // The modal state is now lifted to survive ChartCanvas remounts (tablet double-tap issue)

  // Handle drag state changes (called during order drag)
  const handleDragStateChange = (dragState) => {
    // Update dragging state for OCO connector visibility
    setIsDragging(dragState !== null);

    // Check if drag ended (dragState is null)
    if (dragState === null) {
      // Commit any pending OTO updates for all orders that might have been dragged
      otoRelativeDistances.current.forEach((value, key) => {
        if (key.endsWith("_pending")) {
          const orderId = key.replace("_pending", "");
          const order = orders.find((o) => o.id === orderId);
          if (order && order.state === "open") {
            // Defer commit for open orders — will be applied after confirmation in handleUpdateOrderPrice
            deferredOTOCommits.current.set(orderId, new Map(value));
            otoRelativeDistances.current.delete(key);
          } else {
            commitPendingOTOUpdates(orderId);
          }
        }
      });

      if (onDragStateChange) {
        onDragStateChange(null);
      }
      return;
    }

    // If we have drag state with an order being dragged, update OTO positions in real-time
    if (
      dragState &&
      dragState.index !== undefined &&
      dragState.price !== undefined
    ) {
      const draggedOrder = orders[dragState.index];

      // Skip OTO processing on drag start (just click) - only process on actual movement
      if (dragState.started) {
        if (onDragStateChange) {
          onDragStateChange(dragState);
        }
        return;
      }

      if (draggedOrder && draggedOrder.oto && draggedOrder.oto.length > 0) {
        // Use the current drag price as the main order's "temp" position
        const currentMainPrice = dragState.price;

        // Get or calculate the relative distances for this order's OTO orders
        let relativeDistances = otoRelativeDistances.current.get(
          draggedOrder.id,
        );
        if (!relativeDistances && dragState !== "dragging") {
          // Only recompute base distances if they don't exist yet AND we're not actively dragging
          // This preserves user-intended spacing and prevents distance drift during dragging operations
          const recomputed = new Map();
          draggedOrder.oto.forEach((otoOrder) => {
            // Use effective price: price field, or fall back to limit/stop from router orders
            const effectiveOTOPrice =
              otoOrder.price ?? otoOrder.limit ?? otoOrder.stop;
            const effectiveMainPrice =
              draggedOrder.price ?? draggedOrder.limit ?? draggedOrder.stop;
            const distance = effectiveOTOPrice - effectiveMainPrice;
            recomputed.set(otoOrder.id, distance);
          });
          otoRelativeDistances.current.set(draggedOrder.id, recomputed);
          relativeDistances = recomputed;
        }

        // PERFORMANCE OPTIMIZATION: Store pending OTO updates but don't commit to store during drag
        // This prevents excessive store updates that make drag feel heavy
        // Store both price and deltaTicks to avoid stale price calculations on commit
        const pendingOTOUpdates = new Map();
        draggedOrder.oto.forEach((otoOrder) => {
          let relativeDistance = relativeDistances.get(otoOrder.id);

          // Guard: recompute if cached distance is missing or invalid
          if (relativeDistance === undefined || !isFinite(relativeDistance)) {
            const effectiveOTOPrice =
              otoOrder.price ?? otoOrder.limit ?? otoOrder.stop;
            const effectiveMainPrice =
              draggedOrder.price ?? draggedOrder.limit ?? draggedOrder.stop;
            relativeDistance = effectiveOTOPrice - effectiveMainPrice;
            // Update cache to prevent repeated recomputation
            relativeDistances.set(otoOrder.id, relativeDistance);
          }

          let newOTOPrice = currentMainPrice + relativeDistance;

          // ✅ SANITY CHECK: Use proper OTO validation with symbol-specific tick sizes
          const otoType = otoOrder.id.endsWith("-S") ? "stop" : "profit";
          const validation = validateOTOPrice(
            newOTOPrice,
            currentMainPrice,
            draggedOrder.side,
            otoType,
            symbol,
          );

          if (validation.clamped) {
            newOTOPrice = validation.price;
            console.warn("🛡️ OTO price clamped during main drag:", {
              otoType,
              original: currentMainPrice + relativeDistance,
              clamped: newOTOPrice,
              reason: validation.reason,
              symbol,
            });
          }

          // Preserve the existing deltaTicks from the OTO order, or compute from relative distance.
          // This ensures deltaTicks aren't recalculated with stale main price in updateOTOPrice.
          let deltaTicksToStore = otoOrder.deltaTicks;

          // If deltaTicks not defined, compute from relative distance (which is in price units)
          if (
            deltaTicksToStore === undefined &&
            relativeDistance !== undefined
          ) {
            const tickSize = getTickSize(symbol);
            if (tickSize && tickSize > 0) {
              // Convert price distance to tick count
              deltaTicksToStore = Math.round(relativeDistance / tickSize);
            }
          }

          // Store both price and deltaTicks together
          pendingOTOUpdates.set(otoOrder.id, {
            price: newOTOPrice,
            deltaTicks: deltaTicksToStore,
          });
        });

        // Store pending updates for commit when drag ends
        otoRelativeDistances.current.set(
          `${draggedOrder.id}_pending`,
          pendingOTOUpdates,
        );
        // Instrumentation: log preview vs base distances for debugging visual mismatch
        if (pendingOTOUpdates.size > 0) {
          const preview = {};
          pendingOTOUpdates.forEach((v, k) => {
            preview[k] = v;
          });
          const base = {};
          relativeDistances.forEach((v, k) => {
            base[k] = v;
          });
        }
      }
    }

    if (onDragStateChange) {
      onDragStateChange(dragState);
    }
  };

  // OTO-related handlers
  // Wrap OTO edit to also refresh cached relative distance so custom user-adjusted spacing persists on next main drag
  const handleOTOEdit = async (mainOrderId, otoOrderId, newPrice, options) => {
    const manualEdit = !(options && options.programmatic);

    // Add validation for ALL edits (manual and programmatic) to prevent unnecessary router calls
    const mainOrder = orders.find((o) => o.id === mainOrderId);
    if (mainOrder && mainOrder.oto) {
      const originalOtoOrder = mainOrder.oto.find(
        (oto) => oto.id === otoOrderId,
      );
      if (originalOtoOrder) {
        // Use centralized validation to check if price actually changed
        const validation = validateOrderReplacement({
          dragStart: null,
          dragEnd: null,
          originalOrder: originalOtoOrder,
          newValues: { price: newPrice },
          operationType: "oto-order",
        });

        if (!validation.hasChanges) {
          return; // Skip the update
        }

        // For manual edits on open orders, confirm before sending to broker
        if (manualEdit && mainOrder.state === "open") {
          const otoType = otoOrderId.endsWith("-S") ? "Stop" : "Alvo";
          const confirmed = await confirmOrderModification(
            { ...mainOrder, symbol: mainOrder.symbol || symbol },
            newPrice,
          );
          if (!confirmed) {
            // Revert: restore original OTO price
            const origPrice =
              originalOtoOrder.price ??
              originalOtoOrder.limit ??
              originalOtoOrder.stop;
            if (origPrice != null) {
              updateOTOPrice(mainOrderId, otoOrderId, origPrice, {
                programmatic: true,
                skipRouterCall: true,
              });
            }
            return;
          }
        }
      }
    }

    updateOTOPrice(mainOrderId, otoOrderId, newPrice, options);
    if (!manualEdit) return; // Do not overwrite cache for programmatic reposition

    // Do not update distance cache during active drag operations to preserve user-intended spacing
    if (isDragging) {
      return;
    }

    try {
      const mainOrder2 = orders.find((o) => o.id === mainOrderId);
      if (!mainOrder2) return;
      let relativeDistances = otoRelativeDistances.current.get(mainOrderId);
      if (!relativeDistances) relativeDistances = new Map();
      const distance = newPrice - mainOrder2.price;
      relativeDistances.set(otoOrderId, distance);
      otoRelativeDistances.current.set(mainOrderId, relativeDistances);
      otoRelativeDistances.current.delete(`${mainOrderId}_pending`);
    } catch (e) {
      console.warn(
        "handleOTOEdit: Failed to update relative distance cache",
        e,
      );
    }
  };

  // Handle stops button click - create BOTH stop and profit orders
  const handleStopsClick = (mainOrderId) => {
    // Initialize the price tracking for the main order if not already done
    const mainOrder = orders.find((o) => o.id === mainOrderId);
    if (mainOrder && !lastKnownPrices.current.has(mainOrderId)) {
      lastKnownPrices.current.set(mainOrderId, mainOrder.price);
    }

    try {
      createOTO(mainOrderId);

      // After creating OTO orders, calculate and store their relative distances
      setTimeout(() => {
        const updatedOrder = orders.find((o) => o.id === mainOrderId);
        if (updatedOrder && updatedOrder.oto && updatedOrder.oto.length > 0) {
          const relativeDistances = new Map();
          const effectiveMainPrice =
            updatedOrder.price ?? updatedOrder.limit ?? updatedOrder.stop;
          updatedOrder.oto.forEach((otoOrder) => {
            const effectiveOTOPrice =
              otoOrder.price ?? otoOrder.limit ?? otoOrder.stop;
            const distance = effectiveOTOPrice - effectiveMainPrice;
            relativeDistances.set(otoOrder.id, distance);
          });
          otoRelativeDistances.current.set(mainOrderId, relativeDistances);
        }
      }, 0); // Use setTimeout to ensure OTO orders are created first
    } catch (error) {
      console.error("❌ Failed to create OTO orders:", error);
      alert(`Erro ao criar stop/profit: ${error.message}`);
    }
  };

  // Handle OTO order removal - supports both single order and all OTOs removal
  const handleOTORemove = (arg1, arg2 = "SINGLE") => {
    // Supported call signatures:
    // 1) handleOTORemove(otoOrderId)
    // 2) handleOTORemove(mainOrderId, otoOrderId)
    // 3) handleOTORemove(mainOrderId, 'ALL_STOPS')
    let mainOrderId = null;
    let otoOrderId = null;
    let mode = "SINGLE";

    if (arg2 === "ALL_STOPS") {
      mainOrderId = arg1;
      mode = "ALL_STOPS";
    } else if (arg2 && arg2 !== "SINGLE") {
      // Pattern (mainOrderId, otoOrderId)
      mainOrderId = arg1;
      otoOrderId = arg2;
    } else {
      // Pattern (otoOrderId) only
      otoOrderId = arg1;
    }

    if (mode === "ALL_STOPS") {
      if (mainOrderId) {
        removeAllOTOs(mainOrderId);
        // Proactively clear any cached relative or pending distances for this main order
        try {
          const pendingKey = `${mainOrderId}_pending`;
          const hadRel = otoRelativeDistances.current.delete(mainOrderId);
          // Delete pending map entirely (it is a Map keyed by otoId) – ensures no ghost previews
          const hadPending = otoRelativeDistances.current.delete(pendingKey);
        } catch (e) {
          console.warn(
            "InteractiveOrderIntegration: Failed to clear cached distances after ALL_STOPS removal",
            e,
          );
        }
      }
      return;
    }

    if (!otoOrderId) {
      console.warn(
        "❌ handleOTORemove: Missing otoOrderId, nothing to remove",
        { arg1, arg2 },
      );
      return;
    }

    // Resolve main order if not provided
    let mainOrder = null;
    if (mainOrderId) {
      mainOrder = orders.find((o) => o.id === mainOrderId);
      if (!mainOrder) {
        console.warn(
          "❌ handleOTORemove: Provided mainOrderId not found, attempting lookup by otoOrderId",
          { mainOrderId, otoOrderId },
        );
      }
    }
    if (!mainOrder) {
      mainOrder = orders.find(
        (order) => order.oto && order.oto.some((oto) => oto.id === otoOrderId),
      );
    }

    if (!mainOrder) {
      console.warn(
        "❌ Could not find main order for OTO order (removal failed):",
        { otoOrderId, providedMainOrderId: mainOrderId },
      );
      return;
    }

    removeOTO(mainOrder.id, otoOrderId);

    // Clean cached relative distances & pending previews for that OTO if present
    try {
      const rel = otoRelativeDistances.current.get(mainOrder.id);
      if (rel && rel.delete) rel.delete(otoOrderId);
      const pendingKey = `${mainOrder.id}_pending`;
      const pending = otoRelativeDistances.current.get(pendingKey);
      if (pending && pending.delete) pending.delete(otoOrderId);
    } catch (e) {
      console.warn(
        "handleOTORemove: Failed to prune cached distances for removed OTO",
        e,
      );
    }
  };

  // Don't render if no symbol provided
  if (!symbol) {
    console.warn(
      "InteractiveOrderIntegration: No symbol provided, not rendering",
    );
    return null;
  }

  // // Only log when orders change, not on every render
  // React.useEffect(() => {
  //   if (orders.length > 0) {
  //     // Log OTO data changes for debugging
  //     orders.forEach(order => {
  //       if (order.oto && order.oto.length > 0) {
  //           order.oto.map(oto => `${oto.type}@${oto.price}`).join(', '));
  //       }
  //     });
  //   }
  // }, [orders, symbol]); // Watch the entire orders array, not just length

  // NOTE: ordersKey removed - was causing cascading redraws via synthetic mousemove.
  // React will re-render OrderInteractive when orderList prop changes - no key change needed.

  // Add isPending property to orders for visual feedback during modifications
  // Also deduplicate orders by ID to prevent duplicate key warnings
  const ordersWithPending = useMemo(() => {
    const seenIds = new Set();
    return orders
      .filter((order) => {
        if (seenIds.has(order.id)) {
          return false; // Skip duplicate
        }
        seenIds.add(order.id);
        return true;
      })
      .map((order) => ({
        ...order,
        isPending: pendingModifications.has(order.id),
      }));
  }, [orders, pendingModifications]);

  // Group OCO orders into pairs for connector line rendering
  const ocoOrderPairs = useMemo(() => {
    const ocoGroups = new Map();
    const pairs = [];

    // Group orders by their base OCO UUID (extract common UUID from IDs like "uuid-L" and "uuid-S")
    orders.forEach((order) => {
      if (order.oco) {
        // Extract base UUID from both the order ID and its OCO reference
        // For IDs like "e4c7a75e-0c67-4f4e-975c-afaf873f11d0-L" get "e4c7a75e-0c67-4f4e-975c-afaf873f11d0"
        const baseUuid = order.id.match(/^(.+)-[LS]$/)?.[1] || order.id;

        if (!ocoGroups.has(baseUuid)) {
          ocoGroups.set(baseUuid, []);
        }
        ocoGroups.get(baseUuid).push(order);
      }
    });

    // Create pairs from groups (each group should have exactly 2 orders)
    ocoGroups.forEach((groupOrders, baseUuid) => {
      if (groupOrders.length === 2) {
        pairs.push({
          id: `oco-connector-${baseUuid}`,
          order1: groupOrders[0],
          order2: groupOrders[1],
        });
      } else if (groupOrders.length > 2) {
        console.warn("OCO group has more than 2 orders:", {
          baseUuid,
          count: groupOrders.length,
        });
      } else if (groupOrders.length === 1) {
      }
    });

    return pairs;
  }, [orders]);

  return (
    <>
      <OrderInteractive
        key={`order-interactive-${symbol}`} // Stable key - React updates props without remounting
        symbol={symbol}
        enabled={enabled}
        orderList={ordersWithPending}
        onCreateOrder={handleCreateOrder}
        onUpdateOrderPrice={handleUpdateOrderPrice}
        onUpdateOrderQuantity={handleUpdateOrderQuantity}
        onQuantityEdit={handleQuantityEdit}
        onSubmitOrder={handleSubmitOrder}
        onDeleteOrder={handleDeleteOrder}
        onStopsClick={handleStopsClick}
        onOTORemove={handleOTORemove}
        onOTOEdit={handleOTOEdit}
        onDragStateChange={handleDragStateChange}
        tempOTOPrices={otoRelativeDistances.current} // Pass pending updates for visual positioning
        {...otherProps}
      />

      {/* Render OCO connector lines for linked order pairs - hide during drag operations */}
      {!isDragging &&
        ocoOrderPairs.map(({ id, order1, order2 }) => (
          <OCOConnectorLine
            key={id}
            order1={order1}
            order2={order2}
            allOrders={orders}
          />
        ))}

      {/* Indicator for orders hidden beyond left edge due to stacking */}
      <HiddenOrdersIndicator
        key={`hidden-orders-${symbol}`}
        orders={ordersWithPending}
      />

      {/* Render position line if there's an active position */}
      {position && (
        <PositionLine key={`position-${symbol}`} position={position} />
      )}

      {/* Render position label if there's an active position */}
      {position && (
        <PositionLabel
          key={`position-label-${symbol}`}
          position={position}
          symbol={symbol}
        />
      )}

      {/* NOTE: QuantityEditModal has been moved to ChartWrapper to survive ChartCanvas remounts */}

      {/* Render router login modal using portal to escape SVG context.
          PoC #popout: target the window the chart actually lives in. */}
      <g ref={anchorNodeRef} style={{ display: "none" }} />
      {showLoginModal &&
        ReactDOM.createPortal(
          <RouterLoginModal
            visible={showLoginModal}
            onHide={() => setShowLoginModal(false)}
            onSuccessfulSubmit={() => setShowLoginModal(false)}
          />,
          (anchorNodeRef.current?.ownerDocument ?? document).body,
        )}
    </>
  );
};

InteractiveOrderIntegration.propTypes = {
  symbol: PropTypes.string.isRequired,
  enabled: PropTypes.bool,
  onDragStateChange: PropTypes.func,
  currentPrice: PropTypes.number,
};

export default InteractiveOrderIntegration;
